/*
 * Small most-recent-first list kept in localStorage (recent Quick Track
 * searches, recently assumed users). Storage can be missing or blocked
 * (private windows, disabled site data), so every access is guarded and a
 * failure just means an empty list.
 */

export const RECENT_LIST_MAX = 5;

export type RecentListItem = { id: string };

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

export function readRecentList<T extends RecentListItem>(storageKey?: string | null): T[] {
  const storage = getStorage();
  if (!storage || !storageKey) return [];
  try {
    const parsed = JSON.parse(storage.getItem(storageKey) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/** True once anything (even an empty, cleared list) was saved under the key. */
export function hasStoredRecentList(storageKey?: string | null): boolean {
  const storage = getStorage();
  if (!storage || !storageKey) return false;
  try {
    return storage.getItem(storageKey) !== null;
  } catch (e) {
    return false;
  }
}

export function writeRecentList<T extends RecentListItem>(storageKey: string | null | undefined, items: T[]): void {
  const storage = getStorage();
  if (!storage || !storageKey) return;
  try {
    storage.setItem(storageKey, JSON.stringify(items));
  } catch (e) {
    // Quota or blocked storage — recents are a convenience, ignore.
  }
}

/** Put `item` first, drop older entries with the same id, keep `max`. */
export function addToRecentList<T extends RecentListItem>(
  items: T[] | null | undefined,
  item: T,
  max = RECENT_LIST_MAX,
): T[] {
  const rest = (items || []).filter((existing) => existing.id !== item.id);
  return [item, ...rest].slice(0, max);
}

export function removeFromRecentList<T extends RecentListItem>(
  items: T[] | null | undefined,
  id: string,
): T[] {
  return (items || []).filter((existing) => existing.id !== id);
}

/** Per-user storage keys. Pass the signed-in (not assumed) username for CSRs. */
export function recentTracksStorageKey(username?: string | null): string {
  return username ? `ohana.recentTracks.${username}` : '';
}

export function recentAssumedStorageKey(csrUsername?: string | null): string {
  return csrUsername ? `ohana.recentAssumed.${csrUsername}` : '';
}

/** Drop this CSR's recently assumed users. Quick Track searches are a different key. */
export function clearRecentAssumedUsers(csrUsername?: string | null): void {
  const storage = getStorage();
  const key = recentAssumedStorageKey(csrUsername);
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch (e) {
    // Blocked storage — nothing to clear.
  }
}
